import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { api, clearJsonLd, setJsonLd, setSeo, track } from "@/lib/api";
import { AffiliateDisclosure } from "@/components/site/OfferGrid";
import { SmartImage } from "@/components/site/SmartImage";
import { Loading } from "@/pages/NotFound";

// Dedicated product page. The tracked Awin destination is never written into this file: it is read
// from the offer record so the owner's tracking link stays the single source of truth.
const CATEGORY = "perfect-chair-pc-610";
// The advertiser's original creative URL. The visible image is that same file, mirrored unaltered
// and served through our image pipeline; this URL is still requested as a 1x1 pixel so the
// advertiser's impression tracking keeps firing exactly as their creative intends.
const IMPRESSION_PIXEL = "https://www.awin1.com/cshow.php?s=4244660&v=102823&q=554071&r=3096131";
const REL = "sponsored noopener noreferrer";

const ImpressionPixel = () => (
  /* eslint-disable-next-line no-restricted-syntax -- advertiser impression tracker, not a picture */
  <img
    src={IMPRESSION_PIXEL}
    alt=""
    aria-hidden="true"
    width="1"
    height="1"
    data-testid="pc610-impression-pixel"
    className="absolute w-px h-px opacity-0 pointer-events-none"
  />
);

const FEATURES = [
  {
    h: "Zero-gravity positioning",
    p: "The Perfect Chair reclines into Human Touch's zero-gravity position, which raises the legs above the level of the heart and distributes your weight across the frame.",
  },
  {
    h: "Powered reclining",
    p: "Power recline moves the chair smoothly between upright and fully reclined positions, so you can stop anywhere in between rather than choosing a fixed setting.",
  },
  {
    h: "Adjustable comfort",
    p: "Omni-Motion articulation lets the back and legs adjust through the recline range, and Human Touch offers head and lumbar support accessories for the Perfect Chair line.",
  },
  {
    h: "Upholstery and base options",
    p: "Human Touch offers the PC-610 in a choice of upholsteries and wood base finishes, so the chair can be matched to the room it lives in.",
  },
];

const Cta = ({ href, testId }) => (
  <a
    href={href}
    target="_blank"
    rel={REL}
    data-testid={testId}
    onClick={() =>
      track({
        event_type: "cta_click",
        cta_placement: "PRODUCT PAGE CTA",
        offer_category: CATEGORY,
        merchant: "Human Touch",
        product: "Human Touch Perfect Chair PC-610 Omni-Motion Recliner",
      })
    }
    className="inline-flex items-center gap-2 px-7 py-4 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
  >
    View Perfect Chair at Human Touch <ArrowUpRight size={15} />
  </a>
);

export default function PerfectChairPC610() {
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    setSeo({
      title: "Human Touch Perfect Chair PC-610 Omni-Motion Recliner — Human Home Living",
      description:
        "The Human Touch Perfect Chair PC-610 Omni-Motion Recliner: a premium zero-gravity power recliner with adjustable positioning and a choice of upholsteries.",
    });
    track({ event_type: "page_view", page: "/wellness/human-touch-perfect-chair-pc-610" });
    const origin = window.location.origin;
    const url = `${origin}/wellness/human-touch-perfect-chair-pc-610`;
    // Product markup without price, rating or review: Human Home Living is not the seller and no
    // firsthand testing has taken place, so only manufacturer-stated identity is described.
    setJsonLd("ld-product", {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Human Touch Perfect Chair PC-610 Omni-Motion Recliner",
      brand: { "@type": "Brand", name: "Human Touch" },
      category: "Zero-gravity recliners",
      sku: "PC-610",
      description:
        "A premium zero-gravity power recliner from Human Touch, with powered reclining, " +
        "Omni-Motion adjustable positioning and a choice of upholsteries and base finishes.",
      url,
    });
    setJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: "Wellness & Recovery",
          item: `${origin}/hub/wellness-recovery`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Perfect Chair PC-610 Omni-Motion Recliner",
          item: url,
        },
      ],
    });
    api
      .get("/offers/group", { params: { category: CATEGORY } })
      .then((r) => setOffer(r.data.offers?.[0] || null))
      .catch(() => setOffer(null));
    return () => {
      clearJsonLd("ld-product");
      clearJsonLd("ld-breadcrumb");
    };
  }, []);

  if (!offer) return <Loading label="Loading product" />;
  const href = offer.tracking_url;

  return (
    <article data-testid="pc610-page" className="bg-bone">
      <div className="max-w-[1180px] mx-auto px-5 md:px-8 pt-8 md:pt-12 pb-16 md:pb-24">
        <Link
          to="/hub/wellness-recovery#perfect-chair-pc-610"
          data-testid="pc610-back-to-wellness"
          className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-[#6A665F] hover:text-sage transition-colors"
        >
          <ArrowLeft size={14} /> Back to Wellness
        </Link>

        <div
          id="human-touch-pc610"
          data-testid="pc610-anchor"
          className="grid lg:grid-cols-[52fr_48fr] gap-9 lg:gap-14 items-start mt-7 md:mt-10 scroll-mt-28"
        >
          {/* Advertiser-provided Awin creative, used exactly as supplied. The fixed square frame
              reserves its space so the page cannot shift as the image arrives. */}
          <a
            href={href}
            target="_blank"
            rel={REL}
            data-testid="pc610-image-link"
            onClick={() =>
              track({
                event_type: "cta_click",
                cta_placement: "PRODUCT PAGE IMAGE",
                offer_category: CATEGORY,
                merchant: "Human Touch",
                product: offer.display_name,
              })
            }
            className="block overflow-hidden bg-white border border-line aspect-square focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2"
          >
            <SmartImage
              src={offer.image}
              alt={offer.image_alt || offer.display_name}
              priority
              sizes="(min-width:1024px) 600px, 100vw"
              maxWidth={1024}
              width={2000}
              height={2000}
              data-testid="pc610-awin-image"
              className="w-full h-full object-contain p-4 md:p-8 transition-transform duration-[900ms] ease-out hover:scale-[1.02]"
            />
          </a>
          <ImpressionPixel />

          <div className="lg:pt-4">
            <span className="eyebrow" data-testid="pc610-brand">
              Human Touch
            </span>
            <h1
              className="display text-[34px] sm:text-[42px] lg:text-[50px] leading-[1.06] text-charcoal mt-4"
              data-testid="pc610-title"
            >
              Perfect Chair PC-610 Omni-Motion Recliner
            </h1>
            <p className="text-[17px] leading-[1.7] text-[#454545] font-light mt-5">
              The Perfect Chair PC-610 is Human Touch's premium zero-gravity power recliner. It is a
              comfort recliner rather than a massage chair: the chair reclines under power into a
              zero-gravity position that lifts the legs above the heart, and it can be stopped
              anywhere through that range. It is built for a living room, study or reading corner
              where a chair is expected to look as considered as the rest of the room.
            </p>

            <div className="mt-7 pt-6 border-t border-line" data-testid="pc610-price">
              <div className="display text-[30px] leading-none text-charcoal">
                From $3,299
                <span className="text-[15px] font-light text-[#6A665F] ml-2">starting price</span>
              </div>
              <p className="text-[13px] font-light text-[#8A867F] mt-2.5 leading-relaxed">
                Price verified on humantouch.com on 27 October 2026. Upholstery, base finish and
                accessory choices change the final price, and prices and availability are set by
                Human Touch.
              </p>
            </div>

            <div className="mt-8">
              <Cta href={href} testId="pc610-cta-top" />
            </div>
            <AffiliateDisclosure testId="pc610-disclosure-top" className="mt-5 max-w-xl" />
          </div>
        </div>

        <section className="mt-16 md:mt-24 pt-10 md:pt-14 border-t border-line">
          <span className="eyebrow">Features</span>
          <h2 className="display text-[27px] md:text-[34px] leading-[1.15] text-charcoal mt-4 max-w-2xl">
            What the PC-610 offers
          </h2>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8 mt-9 max-w-[960px]">
            {FEATURES.map((f) => (
              <div key={f.h} data-testid={`pc610-feature-${f.h.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <h3 className="flex items-start gap-2.5 text-[18px] font-medium text-charcoal leading-snug">
                  <Check size={17} className="text-sage-dark shrink-0 mt-[3px]" />
                  {f.h}
                </h3>
                <p className="text-[15.5px] leading-[1.65] text-[#454545] font-light mt-2.5 pl-[27px]">
                  {f.p}
                </p>
              </div>
            ))}
          </div>
          <p className="text-[13.5px] font-light text-[#8A867F] mt-8 max-w-2xl leading-relaxed">
            Specifications, dimensions and available options are set by Human Touch. Confirm the
            configuration, delivery and warranty details for the chair you are considering on the
            manufacturer's own product page before ordering.
          </p>
        </section>

        <section className="mt-14 md:mt-20 bg-white border border-line px-7 md:px-12 py-11 md:py-14">
          <h2 className="display text-[26px] md:text-[32px] leading-[1.15] text-charcoal max-w-xl">
            See the Perfect Chair PC-610 at Human Touch
          </h2>
          <p className="text-[16.5px] leading-[1.65] text-[#454545] font-light mt-4 max-w-2xl">
            Current configurations, upholstery choices, pricing and availability are shown on Human
            Touch's own product page.
          </p>
          <div className="mt-7">
            <Cta href={href} testId="pc610-cta-bottom" />
          </div>
          <AffiliateDisclosure testId="pc610-disclosure-bottom" className="mt-5 max-w-xl" />
          <div className="mt-8 pt-6 border-t border-line">
            <Link
              to="/hub/wellness-recovery"
              data-testid="pc610-wellness-link"
              className="inline-flex items-center gap-2 text-[12.5px] tracking-[0.14em] uppercase text-sage hover:text-sage-dark transition-colors"
            >
              Explore more Wellness & Recovery
            </Link>
          </div>
        </section>
      </div>
    </article>
  );
}
